/**
 * Signed-token wire-format primitives -- the `base64url(payloadJson) + "." + base64url(HMAC-SHA256(payloadBytes, secret))`
 * encoding every HMAC-signed session token shares. Near-pure leaf: its ONLY value import is `asRecordOrNull`
 * (`const/record-guards.ts`), a const->const edge no layer ban reaches and which introduces no capability -- that leaf
 * imports only `const/string-guards.ts`, so the dependency closure stays `node:`-free and cycle-free.
 *
 * Homed in const/ rather than utils/ because EVERY consumer is itself a pure utils/ leaf
 * (`utils/security/app-session-token.ts`, `utils/security/guest-session-token.ts`, `utils/telephony/voice-relay-token.ts`):
 * a utils-to-utils value import is a `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` violation with no allowlist, so const/ is the
 * one shared home the layer floor permits. Same const-leaf-pinned shape as `const/string-case.ts` and `const/array-guards.ts`.
 *
 * Workers-native by construction: the `crypto.subtle` / `btoa` / `atob` / `TextEncoder` globals only, no `node:*` capability.
 */

import { nowEpochSeconds } from '@federa/const/time-units';
import { asRecordOrNull } from '@federa/const/record-guards';

/** Stateless UTF-8 encoder; one instance serves every call. */
const textEncoder = new TextEncoder();

/** Stateless UTF-8 decoder; one instance serves every non-streaming call. */
const textDecoder = new TextDecoder();

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): UTF-8 encoder homed in const/ so pure utils/ leaves can value-import
 * it without a forbidden cross-utils sibling dependency. The single encode seam for both the signed payload JSON and the
 * HMAC secret, so a signer and its verifier cannot disagree on byte representation.
 */
export function utf8Bytes(text: string): Uint8Array<ArrayBuffer> {
	return textEncoder.encode(text);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): UTF-8 decoder homed in const/ so pure utils/ leaves can value-import
 * it without a forbidden cross-utils sibling dependency. Inverse of {@link utf8Bytes}, for reading a signature-verified
 * payload back into JSON text.
 */
export function utf8Text(bytes: Uint8Array): string {
	return textDecoder.decode(bytes);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): base64url encoder homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. UNPADDED base64url (`+` -> `-`, `/` -> `_`,
 * trailing `=` dropped), the inverse of {@link base64UrlToBytes}. Builds the binary string element-wise rather than via
 * `String.fromCharCode.apply`, whose argument list overflows the stack on large inputs.
 */
export function bytesToBase64Url(bytes: Uint8Array): string {
	const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): base64url decoder homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. Returns an EMPTY array on malformed input -- the
 * documented invalid-input sentinel every fail-closed verify path checks before trusting the bytes. The two `replace`s
 * are length-preserving, so the padding remainder reads off the RAW value; a remainder of 1 is unreachable for real
 * base64 and cannot be padded, hence the fail-closed early return.
 */
export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
	const remainder = value.length % 4;
	if (remainder === 1) return new Uint8Array(0);
	const normalized = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - remainder) % 4);
	try {
		return Uint8Array.from(atob(normalized), (character) => character.charCodeAt(0));
	} catch {
		// Silent: malformed base64url on a fail-closed verify path -- the empty byte array is the documented invalid-input sentinel.
		return new Uint8Array(0);
	}
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): HMAC-SHA256 signer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. Returns the RAW signature bytes; callers encode
 * them with {@link bytesToBase64Url} and compare them with {@link timingSafeEqual}. Both byte parameters are
 * ArrayBuffer-backed (never SharedArrayBuffer), which is what `crypto.subtle` accepts as a `BufferSource`.
 */
export async function hmacSha256(payloadBytes: Uint8Array<ArrayBuffer>, secret: string): Promise<Uint8Array<ArrayBuffer>> {
	const key = await crypto.subtle.importKey('raw', utf8Bytes(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
	const signature = await crypto.subtle.sign('HMAC', key, payloadBytes);
	return new Uint8Array(signature);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): constant-time comparator homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. Compares every byte regardless of where the first
 * mismatch falls, so signature verification leaks no timing oracle. Length inequality returns early -- a token's length
 * is public, so that branch reveals nothing a caller does not already hold.
 */
export function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
	if (a.length !== b.length) return false;
	let diff = 0;
	for (let i = 0; i < a.length; i++) diff |= (a[i] as number) ^ (b[i] as number);
	return diff === 0;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): expiry predicate homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. FAIL-CLOSED on the claim as well as the clock: a
 * missing, non-numeric or NaN `exp` is EXPIRED, never "no expiry set", so a payload that lost the claim cannot outlive
 * its token. The boundary is strict (`exp > now`), so a token expiring exactly this second is already expired -- both
 * signed-session consumers verified it that way and the tie must not silently flip to valid.
 */
export function isUnexpiredEpochSeconds(exp: unknown): boolean {
	return typeof exp === 'number' && exp > nowEpochSeconds();
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the verified payload when it is PRESENT and its `exp` claim is
 * unexpired ({@link isUnexpiredEpochSeconds}), else null -- the first claims gate every exp-bearing verifier opens with.
 * VALUE-RETURNING rather than a boolean so the caller keeps its non-null narrowing past the gate.
 */
export function unexpiredPayloadOrNull(payload: Record<string, unknown> | null): Record<string, unknown> | null {
	return payload !== null && isUnexpiredEpochSeconds(payload.exp) ? payload : null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the ENCODE half of the wire format -- JSON-serialize `payload`,
 * HMAC it, and emit `base64url(payloadJson).base64url(signature)`. Homed here for the same reason as the atoms it
 * composes: both signers are pure utils/ leaves that cannot value-import a utils sibling. Every claim set rides this one
 * assembly, so a signer cannot disagree with {@link verifySignedToken} on separator, order or encoding.
 */
export async function encodeSignedToken(payload: unknown, secret: string): Promise<string> {
	const payloadBytes = utf8Bytes(JSON.stringify(payload));
	const signature = await hmacSha256(payloadBytes, secret);
	return `${bytesToBase64Url(payloadBytes)}.${bytesToBase64Url(signature)}`;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the VERIFY half -- split the two parts, decode both, recompute the
 * HMAC and compare it in constant time. Returns the SIGNATURE-VERIFIED payload bytes, or `null` on any failure
 * (fail-closed): wrong part count, either half decoding to the empty invalid-input sentinel, or a signature mismatch.
 *
 * SCOPE: signature only. It deliberately does NOT check expiry, claim shape, or a blank `secret` -- each stays at the
 * caller, which owns its own claim set (`sub` for app sessions, `gid`/`cid` for guest sessions). BOTH consumers reject a
 * blank secret before reaching here, each ahead of its own call, so neither relies on this function for it: a blank
 * secret verifies NOTHING, since `hmacSha256` would key on the empty string and accept anything signed the same way.
 * Callers MUST still run their own expiry + claims gate on the bytes this returns; verified bytes are not an authorized
 * caller.
 */
export async function verifySignedToken(token: string, secret: string): Promise<Uint8Array<ArrayBuffer> | null> {
	const parts = token.split('.');
	if (parts.length !== 2) return null;
	const [encodedPayload, encodedSignature] = parts as [string, string];
	const payloadBytes = base64UrlToBytes(encodedPayload);
	if (payloadBytes.length === 0) return null;
	const providedSignature = base64UrlToBytes(encodedSignature);
	if (providedSignature.length === 0) return null;
	const expectedSignature = await hmacSha256(payloadBytes, secret);
	return timingSafeEqual(providedSignature, expectedSignature) ? payloadBytes : null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the PARSE half of the wire format -- JSON-decode the bytes
 * {@link verifySignedToken} returned and narrow the result to a plain record, or `null`.
 *
 * FAIL-CLOSED ON BOTH ARMS, and that pairing is the contract callers rely on: malformed JSON throws inside `JSON.parse`
 * and is absorbed to `null` here, while a well-formed body that is not an object (`5`, `"x"`, `null`) is rejected by
 * `asRecordOrNull`. A caller therefore never sees a throw and never sees a non-record -- so a claims gate may read
 * members off the result without re-testing that it is one.
 *
 * DELIBERATELY RETURNS A RECORD, NEVER THE CALLER'S CLAIMS INTERFACE. A signature proves WHO wrote the bytes, never that
 * they carry the members the caller expects, so an `as <Payload>` cast here would make every downstream `payload.sub` /
 * `payload.exp` read a compile-time-clean lie, and a body of `null`, `"x"` or `[]` would make the member access itself
 * throw (`QUAL-UNSAFE-INGRESS-001`). Each caller narrows its own claims off the returned record.
 *
 * `asRecordOrNull` admits an ARRAY (a typeof-object test), which is deliberate rather than an oversight: every caller's
 * claims check re-tests each member it reads, so an array fails there on its absent `exp`. This is the SHAPE gate, not
 * the value gate.
 *
 * WHY IT IS HERE AND NOT THREE TIMES. The app-session and guest-session parsers were byte-identical twins that had
 * already DIVERGED once -- one kept an `as <Payload>` cast while the other took the guard cure, the parity gap
 * `QUAL-UNSAFE-INGRESS-001` reported on both and only one closed -- and they were re-converged by hand, which fixes an
 * instance and not the class. The telephony relay parser was a third copy neither of them named. One body here is what
 * makes the next divergence unrepresentable.
 */
export function parseSignedTokenPayload(payloadBytes: Uint8Array): Record<string, unknown> | null {
	try {
		const parsed: unknown = JSON.parse(utf8Text(payloadBytes));
		return asRecordOrNull(parsed);
	} catch {
		// Silent: malformed JSON behind a valid signature is still an unusable claim set on a fail-closed verify path.
		return null;
	}
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the whole PRE-CLAIMS half of a verify, spelled once -- blank token
 * and blank secret rejected, signature checked by {@link verifySignedToken}, bytes narrowed by
 * {@link parseSignedTokenPayload}. Returns the signature-verified claims RECORD, or `null` on any of those arms.
 *
 * THE BLANK-SECRET REJECTION IS THE REASON THIS EXISTS AND IS NOT INSIDE {@link verifySignedToken}. An empty HMAC key
 * verifies NOTHING -- `hmacSha256` keys on the empty string and accepts anything signed the same way -- so every
 * consumer had to run the guard itself, ahead of its own call, and all three (`utils/security/app-session-token.ts`,
 * `utils/security/guest-session-token.ts`, `utils/telephony/voice-relay-token.ts`) re-spelled the same four-line
 * prelude. A guard whose correctness depends on N callers continuing to remember it is one caller away from an
 * unconfigured deployment accepting forgeries; this composition makes forgetting it unrepresentable. `verifySignedToken`
 * keeps its narrower signature-only contract for the callers that hold already-validated inputs.
 *
 * STILL NOT AN AUTHORIZED CALLER: the returned record is signature-verified, not claims-checked. Expiry, audience and
 * subject gates stay at each consumer, which owns its own claim set.
 */
export async function verifySignedTokenPayload(token: string, secret: string): Promise<Record<string, unknown> | null> {
	if (typeof token !== 'string' || !token) return null;
	if (typeof secret !== 'string' || !secret) return null;
	const payloadBytes = await verifySignedToken(token, secret);
	if (payloadBytes === null) return null;
	return parseSignedTokenPayload(payloadBytes);
}
